import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { Hicon, type HiconName } from './Hicon'
import { FiestaBrandLogo } from './FiestaBrandLogo'
import { panelIdForNavTo } from '../permissions/panels'
import { usePanelAccess } from '../permissions/usePanelAccess'
import { useBiUiVisibility, type ToggleableNavId } from '../state/BiUiVisibilityContext'

export const BI_DRAWER_WIDTH = 260

type NavItem = {
  to: string
  label: string
  exact?: boolean
  icon: HiconName
  /** Se definido, o item pode ser ocultado via Ctrl+H. */
  toggleId?: ToggleableNavId
}

/** Ordem completa Business (visível + ocultáveis via Ctrl+H). */
export const BI_NAV_ALL: NavItem[] = [
  { to: '/', label: 'Vendas Diárias', exact: true, icon: 'home' },
  { to: '/produtos', label: 'Vendas por Anúncio', icon: 'category' },
  { to: '/margem-anuncios', label: 'Margem por Anúncio', icon: 'activity' },
  { to: '/natal-2026', label: 'Natal 2026', icon: 'tree' },
  { to: '/margens', label: 'Margens', icon: 'graph', toggleId: 'margens' },
  { to: '/operacoes', label: 'Operações', icon: 'sync', toggleId: 'operacoes' },
  { to: '/reclamacoes', label: 'Reclamações', icon: 'report' },
  { to: '/recompra', label: 'Recompra', icon: 'buy' },
  { to: '/promocoes', label: 'Promoções', icon: 'percent' },
  { to: '/ads', label: 'Ads', icon: 'percent' },
  { to: '/score-anuncios', label: 'Score', icon: 'activity' },
  { to: '/validador-anuncios', label: 'Saúde dos Anúncios', icon: 'document' },
  { to: '/estoque-full', label: 'Estoque Full', icon: 'archive' },
  { to: '/vigia-esgotados', label: 'Vigia de esgotados', icon: 'report' },
  { to: '/expedicao', label: 'Expedição', icon: 'bag' },
  { to: '/fabrica-anuncios', label: 'Fábrica de Anúncios', icon: 'pencil' },
  { to: '/fabrica-videos', label: 'Fábrica de Vídeos', icon: 'pencil' },
  { to: '/agentes', label: 'Agentes', icon: 'sync' },
  { to: '/buybox', label: 'Buy Box', icon: 'percent' },
  { to: '/marcas', label: 'Consulta Marcas INPI', icon: 'archive' },
  { to: '/usuarios', label: 'Usuários', icon: 'group' },
  // Fora da ordem principal — reactiváveis no Ctrl+H
  { to: '/pedidos', label: 'Pedidos', icon: 'bag', toggleId: 'pedidos' },
  { to: '/fiscal', label: 'Fiscal', icon: 'document', toggleId: 'fiscal' },
  { to: '/agenda', label: 'Agenda', icon: 'clock', toggleId: 'agenda' },
  { to: '/pagamentos', label: 'Pagamentos', icon: 'payment', toggleId: 'pagamentos' },
  { to: '/fretes', label: 'Fretes', icon: 'map', toggleId: 'fretes' },
  { to: '/despesas', label: 'Despesas', icon: 'wallet', toggleId: 'despesas' },
  { to: '/clientes', label: 'Clientes', icon: 'user-tag', toggleId: 'clientes' },
]

/** Abas Insights — PDF 12–21 adaptado ao ecommerce Fiesta. */
export const INSIGHTS_NAV_ALL: NavItem[] = [
  { to: '/insights', label: 'Insights', exact: true, icon: 'activity' },
  { to: '/insights/alertas', label: 'Alertas', icon: 'report' },
  { to: '/insights/prioridades', label: 'Prioridades', icon: 'document' },
  { to: '/insights/comercial', label: 'Comercial', icon: 'buy' },
  { to: '/insights/produtos', label: 'Mix & SKUs', icon: 'category' },
  { to: '/insights/logistica', label: 'Logística', icon: 'map' },
  { to: '/insights/financeiro', label: 'Financeiro', icon: 'wallet' },
]

export const BI_NAV_PRIMARY = BI_NAV_ALL.slice(0, 6)
export const BI_NAV_MORE = BI_NAV_ALL.slice(6)
export const BI_NAV = BI_NAV_ALL.map((item) => ({
  ...item,
  exact: item.exact ?? false,
}))

export function isInsightsPath(pathname: string) {
  return pathname === '/insights' || pathname.startsWith('/insights/')
}

function pathActive(pathname: string, to: string, exact?: boolean) {
  return exact ? pathname === to : pathname === to || pathname.startsWith(`${to}/`)
}

/** Tokens alinhados ao sidebar do Financeiro (S2 MainLayout). */
function sidebarTokens(mode: 'light' | 'dark') {
  if (mode === 'dark') {
    return {
      bg: '#171717',
      text: 'rgba(255, 255, 255, 0.92)',
      muted: '#9e9e9e',
      border: 'rgba(255, 255, 255, 0.1)',
      hover: 'rgba(255, 255, 255, 0.06)',
      activeBg: 'rgba(66, 165, 245, 0.18)',
      accent: '#64b5f6',
      accentLight: '#90caf9',
      shadow: '1px 0 0 rgba(255, 255, 255, 0.1), 4px 0 28px rgba(0, 0, 0, 0.45)',
    }
  }
  return {
    bg: '#f1f5f9',
    text: '#334155',
    muted: '#64748b',
    border: '#e2e8f0',
    hover: 'rgba(37, 99, 235, 0.08)',
    activeBg: 'rgba(37, 99, 235, 0.12)',
    accent: '#2563eb',
    accentLight: '#3b82f6',
    shadow: '1px 0 0 #e2e8f0, 4px 0 24px rgba(15, 23, 42, 0.05)',
  }
}

type DomainNavProps = {
  /** Fecha o drawer temporary no mobile após navegar. */
  onNavigate?: () => void
}

/**
 * Sidebar vertical de domínios (Business / Insights) — estilo Financeiro.
 */
export function DomainNav({ onNavigate }: DomainNavProps) {
  const theme = useTheme()
  const { pathname } = useLocation()
  const { prefs } = useBiUiVisibility()
  const { canView, isAdmin } = usePanelAccess()
  const navAll = (isInsightsPath(pathname) ? INSIGHTS_NAV_ALL : BI_NAV_ALL).filter((item) => {
    if (!('toggleId' in item) || !item.toggleId) {
      /* ok */
    } else if (prefs.nav[item.toggleId] === false) {
      return false
    }
    if (isInsightsPath(pathname)) return true
    const panelId = panelIdForNavTo(item.to)
    if (!panelId) return true
    if (panelId === 'usuarios') return isAdmin || canView('usuarios')
    return canView(panelId)
  })
  const insights = isInsightsPath(pathname)
  const s = sidebarTokens(theme.palette.mode === 'dark' ? 'dark' : 'light')

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: s.bg,
        color: s.text,
        boxShadow: s.shadow,
        borderRight: 'none',
      }}
    >
      <Box
        sx={{
          minHeight: { xs: 64, sm: 80 },
          px: 2,
          pt: { xs: 1.5, sm: 2 },
          pb: { xs: 1.15, sm: 1.35 },
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
          borderBottom: `1px solid ${s.border}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0, width: '100%' }}>
          <FiestaBrandLogo height={44} sx={{ maxWidth: 168 }} />
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: s.muted,
              display: 'block',
              lineHeight: 1.2,
              fontWeight: 600,
              letterSpacing: 0.02,
              ml: 0.5,
            }}
          >
            {insights ? 'Insights' : 'Business'}
          </Typography>
        </Box>
      </Box>

      <List sx={{ flex: 1, overflow: 'auto', py: 2, px: 0 }}>
        {navAll.map((item) => {
          const active = pathActive(pathname, item.to, item.exact)
          return (
            <ListItem key={item.to} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                component={RouterLink}
                to={item.to}
                selected={active}
                disableRipple
                onClick={() => onNavigate?.()}
                aria-current={active ? 'page' : undefined}
                sx={{
                  mx: 0,
                  mr: '0.75rem',
                  borderRadius: '0 10px 10px 0',
                  py: 1.5,
                  px: 3,
                  gap: '0.75rem',
                  color: active ? s.accent : s.muted,
                  fontWeight: active ? 600 : 500,
                  transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: s.hover,
                    color: s.text,
                  },
                  '&.Mui-selected': {
                    bgcolor: s.activeBg,
                    color: s.accent,
                    borderRight: `3px solid ${s.accent}`,
                    '&:hover': {
                      bgcolor: s.activeBg,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                  <Hicon name={item.icon} sx={{ fontSize: 22, color: 'inherit' }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: active ? 600 : 500,
                    fontSize: '0.875rem',
                    noWrap: true,
                    fontFamily: '"Outfit", sans-serif',
                  }}
                />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
    </Box>
  )
}
